<template>
  <section
    class="lead-flow-column ui-kanban-column"
    :class="{ 'lead-flow-column--last': lastStage }"
  >
    <LeadFlowStageMarker :code="column.code" :last="lastStage" />

    <header class="lead-flow-column__header ui-kanban-column__header">
      <div class="lead-flow-column__title ui-kanban-column__title">
        <strong><AppTableText :text="column.name" /></strong>
        <AppStatusBadge design="new" compact tone="neutral" :label="String(column.count)" />
      </div>
      <p v-if="column.description"><AppTableText :text="column.description" /></p>
    </header>

    <AppContentSkeleton v-if="initialLoading" layout="block" :label="'Loading ' + column.name" />
    <AppErrorState v-else-if="error && !cards.length" message="Unable to load this column." @retry="load(true)" />
    <div v-else-if="!cards.length" class="lead-flow-column__empty ui-kanban-column__empty">
      <img :src="emptyState.icon" alt="" aria-hidden="true">
      <div>
        <p class="lead-flow-column__empty-title ui-kanban-column__empty-title">{{ emptyState.title }}</p>
        <p class="lead-flow-column__empty-description ui-kanban-column__empty-description">{{ emptyState.description }}</p>
      </div>
    </div>

    <LeadFlowCard
      v-for="lead in cards"
      :key="lead.id"
      :lead="lead"
      :show-stage="(column.stage_codes || []).length > 1"
      :now="now"
      @open="$emit('open', $event)"
    />

    <button
      v-if="hasMore && !(error && cards.length)"
      class="lead-flow-column__more ui-kanban-column__more"
      type="button"
      :disabled="loadingMore"
      @click="load(false)"
    >
      {{ loadingMore ? 'Loading...' : 'Show more' }}
    </button>

    <p v-if="error && cards.length" class="lead-flow-column__append-error ui-kanban-column__append-error">
      Loading failed.
      <button type="button" @click="retryFailedLoad">Retry</button>
    </p>
  </section>
</template>

<script>
import AppContentSkeleton from '@/components/AppContentSkeleton.vue'
import AppErrorState from '@/components/AppErrorState.vue'
import AppTableText from '@/components/AppTableText.vue'
import LeadFlowCard from '@/views/crm/task-tracker/LeadFlowCard.vue'
import LeadFlowStageMarker from '@/views/crm/task-tracker/LeadFlowStageMarker.vue'

const EMPTY_STATES = {
  make_decision: {
    icon: require('@/assets/img/lead-flow/stage-new-leads.png'),
    title: 'No leads awaiting a decision',
    description: 'Assigned leads will appear here while a manager reviews the request.'
  },
  first_contact: {
    icon: require('@/assets/img/lead-flow/stage-first-contact.png'),
    title: 'No leads in this stage',
    description: 'Assigned leads will appear here when they are ready for the first contact.'
  },
  qualification: {
    icon: require('@/assets/img/lead-flow/stage-new-leads.png'),
    title: 'No leads in this stage',
    description: 'Leads will appear here when they are ready for qualification.'
  },
  prepare_offer: {
    icon: require('@/assets/img/lead-flow/stage-prepare-offer.png'),
    title: 'No leads in this stage',
    description: 'Qualified leads will appear here when they are ready for an offer.'
  },
  sell_offer: {
    icon: require('@/assets/img/lead-flow/stage-sell-offer.png'),
    title: 'No leads in this stage',
    description: 'Accepted offers will move here for negotiation.'
  },
  sell_invoice: {
    icon: require('@/assets/img/lead-flow/stage-sell-invoice.png'),
    title: 'No leads in this stage',
    description: 'Invoices will be issued once agreements are confirmed.'
  }
}

export default {
  name: 'LeadFlowColumn',
  components: {
    AppContentSkeleton,
    AppErrorState,
    AppTableText,
    LeadFlowCard,
    LeadFlowStageMarker
  },
  props: {
    column: {
      type: Object,
      required: true
    },
    filters: {
      type: Object,
      required: true
    },
    generation: {
      type: Number,
      required: true
    },
    loadCards: {
      type: Function,
      default: null
    },
    lastStage: {
      type: Boolean,
      default: false
    },
    now: {
      type: Number,
      default: () => Date.now()
    }
  },
  data() {
    return {
      cards: [],
      nextCursor: null,
      hasMore: false,
      initialLoading: false,
      loadingMore: false,
      refreshing: false,
      error: null,
      errorMode: null,
      requestGeneration: 0
    }
  },
  watch: {
    generation: {
      immediate: true,
      handler() {
        this.reset()
        if (this.loadCards) this.load(true)
      }
    }
  },
  computed: {
    emptyState() {
      return EMPTY_STATES[this.column.code] || EMPTY_STATES.qualification
    }
  },
  beforeDestroy() {
    this.requestGeneration += 1
  },
  methods: {
    async load(reset, preserveCards = false) {
      if (!this.loadCards || (reset ? this.initialLoading || this.refreshing : this.loadingMore)) return
      if (!reset && (!this.hasMore || !this.nextCursor)) return

      const requestGeneration = reset ? this.requestGeneration + 1 : this.requestGeneration
      this.requestGeneration = requestGeneration

      if (reset) {
        this.initialLoading = !preserveCards
        this.refreshing = preserveCards
        if (!preserveCards) this.cards = []
        this.nextCursor = null
        this.hasMore = false
      } else {
        this.loadingMore = true
      }
      this.error = null
      this.errorMode = null

      try {
        const response = await this.loadCards({
          ...this.filters,
          column: this.column.code,
          page_size: 6,
          ...(!reset && this.nextCursor ? { cursor: this.nextCursor } : {})
        })
        if (requestGeneration !== this.requestGeneration) return

        const data = response.data || response
        if (data.column && data.column !== this.column.code) {
          throw new Error('Unexpected task tracker column response')
        }

        this.cards = this.mergeCards(data.results, reset)
        this.nextCursor = data.next_cursor || null
        this.hasMore = Boolean(data.has_more && this.nextCursor)
      } catch (error) {
        if (requestGeneration === this.requestGeneration) {
          this.error = error
          this.errorMode = reset ? (preserveCards ? 'refresh' : 'initial') : 'more'
        }
      } finally {
        if (requestGeneration === this.requestGeneration) {
          this.initialLoading = false
          this.loadingMore = false
          this.refreshing = false
        }
      }
    },
    refresh() {
      return this.load(true, Boolean(this.cards.length))
    },
    updateCard(card) {
      if (!card || !card.id) return false
      const index = this.cards.findIndex(item => Number(item.id) === Number(card.id))
      if (index === -1) return false
      this.$set(this.cards, index, card)
      return true
    },
    retryFailedLoad() {
      return this.errorMode === 'more' ? this.load(false) : this.refresh()
    },
    mergeCards(incomingCards, reset) {
      const cards = Array.isArray(incomingCards) ? incomingCards : []
      const seenIds = new Set(reset ? [] : this.cards.map(item => item.id))
      const uniqueCards = cards.filter(card => {
        if (!card || seenIds.has(card.id)) return false
        seenIds.add(card.id)
        return true
      })
      return reset ? uniqueCards : this.cards.concat(uniqueCards)
    },
    reset() {
      this.requestGeneration += 1
      this.cards = []
      this.nextCursor = null
      this.hasMore = false
      this.initialLoading = false
      this.loadingMore = false
      this.refreshing = false
      this.error = null
      this.errorMode = null
    }
  }
}
</script>
